import RoundButton from '@basic/RoundButton';
import GoalLogo from '@components/goals/GoalLogo';
import { DEFAULT_LANDING_PAGE_ACCENT_COLOR } from '@consts';
import { setupForDark } from '@theme';
import classNames from 'classnames';
import AnimatedSection from '../../animations/AnimatedSection';
import { usePaymentAndOrder } from '../../payments/PaymentAndOrderContext';
import GoalLandingPageJumpToMainCTAButton from './GoalLandingPageJumpToMainCTAButton';
import GoalLandingPageMenuBar from './GoalLandingPageMenuBar';
import { parseHighlightedText } from './highlighters';
import { useLandingPageTranslation } from './useLandingPageTranslation';

export default function GoalLandingPageHeroSection() {
  const { state, isSoldOut } = usePaymentAndOrder();
  const hero = state.goal?.landingPage?.hero;
  const title = hero?.title ?? state.goal?.title;
  const shortDescription = hero?.shortDescription ?? state.goal?.shortDescription;
  const accentColor = state.goal?.landingPage?.accentColor || DEFAULT_LANDING_PAGE_ACCENT_COLOR;
  const parsedTitle = parseHighlightedText(title, 'text-white', undefined, accentColor);
  const { t } = useLandingPageTranslation();

  return (
    <div
      className={classNames(
        'w-full flex flex-col gap-md @[1024px]:gap-huge bg-gradient-to-r from-landing-hero-from to-landing-hero-to',
        // RESPONSIVE STYLES
        'pt-none @[640px]:pt-xxxl pb-none'
      )}
    >
      <GoalLandingPageMenuBar />

      <div
        className={classNames(
          'w-full max-w-[1200px] mx-auto flex items-end justify-between',
          // RESPONSIVE STYLES
          'flex-col @[1024px]:flex-row',
          'px-sm @[640px]:px-xl @[1280px]:px-none'
        )}
      >
        <div
          className={classNames(
            'flex flex-col gap-xxl w-full pb-lg',
            // RESPONSIVE STYLES
            'pt-none @[1024px]:pt-[60px]',
            'max-w-full @[1024px]:max-w-[600px]'
          )}
        >
          <div
            className={classNames(
              'w-full font-bold leading-[1.3]',
              setupForDark('text-white'),
              // RESPONSIVE STYLES
              'text-center @[1024px]:text-left text-[32px] @[480px]:text-[40px] @[640px]:text-[60px] break-words'
            )}
          >
            <div className='inline-block align-middle mr-sm @[640px]:mr-md @[1024px]:float-left'>
              <div className='@[640px]:hidden'>
                <GoalLogo logoSvg={state.goal?.logoSvg} size={44} animateCarousel />
              </div>
              <div className='hidden @[640px]:block'>
                <GoalLogo logoSvg={state.goal?.logoSvg} size={64} animateCarousel />
              </div>
            </div>
            {parsedTitle}
          </div>

          <div className='flex flex-col'>
            <div
              className={classNames(
                'font-normal leading-[1.5] whitespace-pre-wrap',
                setupForDark('text-white'),
                // RESPONSIVE STYLES
                'text-center @[1024px]:text-left text-[20px] @[640px]:text-[30px]'
              )}
            >
              {parseHighlightedText(shortDescription, 'text-white', undefined, accentColor)}
            </div>
          </div>

          <div
            className={classNames(
              'flex gap-md items-start',
              // RESPONSIVE STYLES
              'flex-col @[640px]:flex-row',
              'w-full @[640px]:w-auto',
              'justify-center @[1024px]:justify-start'
            )}
          >
            <GoalLandingPageJumpToMainCTAButton type='hero' />

            {state.goal?.landingPage?.modules && !isSoldOut && (
              <RoundButton
                onClick={() => {
                  const element = document.getElementById('toc');
                  element?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }}
                type='empty'
                size='large'
                className='w-full @[640px]:w-auto'
              >
                {hero?.ctaSecondaryLabel ?? t('domains.payments.CTAs.programView')}
              </RoundButton>
            )}
          </div>
        </div>

        {/* Right Column - Hero Image */}
        {state.goal?.landingPage?.hero?.imageUrl && (
          <div
            className={classNames(
              'relative shrink-0 flex justify-center items-center overflow-hidden',
              // RESPONSIVE STYLES
              'w-full @[1024px]:w-auto'
            )}
          >
            <AnimatedSection>
              <img
                src={state.goal?.landingPage?.hero?.imageUrl}
                alt={state.goal?.title}
                className='h-auto w-full max-w-[400px] @[640px]:max-w-[600px] max-h-[400px] @[640px]:max-h-[600px] object-contain'
              />
            </AnimatedSection>
          </div>
        )}
      </div>
    </div>
  );
}
